import { getRegisteredAccessToken } from './auth';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Transforme une URL relative d'image (ex: /api/image-proxy?blob=...) en URL absolue.
 * Retourne undefined si l'URL est vide.
 */
export function getImageUrl(relativeUrl: string | undefined): string | undefined {
  if (!relativeUrl) return undefined;
  if (relativeUrl.startsWith('/api/image-proxy')) return `${BASE_URL}${relativeUrl}`;
  return relativeUrl;
}

/**
 * Fetch authentifié d'une image via /api/image-proxy — retourne l'URL SAS finale (pas un blob).
 * Pour les <img src=...> directes hors proxy, utiliser getImageUrl().
 */
/**
 * Fetch authentifie (Bearer) d'une ressource BINAIRE servie par l'app — retourne
 * un object URL (`URL.createObjectURL`). Ne CASSE JAMAIS (undefined si pas de
 * token / reponse non-ok). CHORE-FBK-PJ-PROXY : les PJ feedback sont servies a
 * travers l'app (le pare-feu storage bloque les navigateurs) — un `<img src>` nu
 * n'envoie pas le Bearer, on materialise donc un object URL. L'appelant gere le
 * cycle de vie (revoke ou cache de page — un object URL n'expire pas).
 */
export async function fetchAuthenticatedBlobObjectUrl(
  path: string | undefined,
): Promise<string | undefined> {
  if (!path) return undefined;
  // CHEMINS RELATIFS D'APP UNIQUEMENT (security-review 2026-06-12) : une URL
  // absolue recevrait le Bearer GS — canal d'exfiltration de token si un path
  // devenait influencable. Fail-soft : undefined.
  if (/^[a-z][a-z0-9+.-]*:|^\/\//i.test(path)) return undefined;
  const resolved = `${BASE_URL}${path}`;
  const getToken = getRegisteredAccessToken();
  if (!getToken) return undefined;
  const token = await getToken();
  const res = await fetch(resolved, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return undefined;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export async function fetchAuthenticatedImageObjectUrl(
  imageUrlFromItem: string | undefined
): Promise<string | undefined> {
  const resolved = getImageUrl(imageUrlFromItem);
  if (!resolved) return undefined;
  if (!resolved.includes('/api/image-proxy')) return resolved;
  const getToken = getRegisteredAccessToken();
  if (!getToken) return undefined;
  const token = await getToken();
  const res = await fetch(resolved, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return undefined;
  try {
    const data = (await res.json()) as { url?: string };
    return typeof data.url === 'string' ? data.url : undefined;
  } catch {
    return undefined;
  }
}
